const apiBase = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
const tokenKey = "khaongaan-session";

export type SessionUser = {
  id: string;
  code: string;
  fullName: string;
  position: string;
  role: "employee" | "manager" | "owner";
  mustChangePin: boolean;
};

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export function getToken() {
  return localStorage.getItem(tokenKey);
}

export function clearToken() {
  localStorage.removeItem(tokenKey);
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const response = await fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init?.body instanceof FormData ? {} : { "content-type": "application/json" }),
      ...init?.headers
    }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "ไม่สามารถเชื่อมต่อระบบได้" })) as { error?: string };
    if (response.status === 401 && path !== "/api/auth/login") clearToken();
    throw new ApiError(payload.error ?? "ไม่สามารถทำรายการได้", response.status);
  }
  return response.json() as Promise<T>;
}

export async function login(code: string, pin: string) {
  const result = await api<{ token: string; expiresAt: string; user: SessionUser }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ code, pin })
  });
  localStorage.setItem(tokenKey, result.token);
  return result.user;
}

export async function getSession() {
  if (!getToken()) return null;
  try {
    const result = await api<{ user: SessionUser }>("/api/auth/me");
    return result.user;
  } catch {
    clearToken();
    return null;
  }
}

export async function logout() {
  try {
    await api<{ success: true }>("/api/auth/logout", { method: "POST" });
  } finally {
    clearToken();
  }
}

export function changePin(currentPin: string, newPin: string) {
  return api<{ success: true }>("/api/auth/change-pin", { method: "POST", body: JSON.stringify({ currentPin, newPin }) });
}

export async function getServerTime(): Promise<string> {
  try {
    const result = await api<{ now: string }>("/api/time");
    return result.now;
  } catch {
    return new Date().toISOString();
  }
}

export function getToday() {
  return api<{
    user: SessionUser;
    date: string;
    workHours: { start_time: string; end_time: string; break_minutes: number; late_grace_minutes: number; ot_threshold_minutes: number };
    attendance: { id: string; work_date: string; check_in_at: string | null; check_out_at: string | null } | null;
    nextAction: "check-in" | "check-out" | "complete";
  }>("/api/me/today");
}

export function checkLocation(latitude: number, longitude: number) {
  const params = new URLSearchParams({ latitude: String(latitude), longitude: String(longitude) });
  return api<{ inside: boolean; distance: number; radius: number }>(`/api/location/check?${params}`);
}

export async function submitAttendance(payload: { photo: Blob; latitude: number; longitude: number; action: "check-in" | "check-out" }) {
  const form = new FormData();
  form.append("photo", payload.photo, `attendance-${Date.now()}.jpg`);
  form.append("latitude", String(payload.latitude));
  form.append("longitude", String(payload.longitude));
  form.append("action", payload.action);
  return api<{ success: true; recordedAt: string; distance: number; action: "check-in" | "check-out" }>("/api/attendance", { method: "POST", body: form });
}
